import {InputElement} from "./input-element.js";

/**
 * Represents a specialized input element that handles checkbox behaviors and related interactions.
 * This class extends the functionality of the `InputElement` class, providing checkbox-specific
 * mechanics, such as managing multiselect values, updating the checked state, and rendering custom
 * component wrappers.
 */
export class CheckElement extends InputElement {
    checkComponent;

    /**
     * Constructs an instance of the class, initializing it with the provided form data, parent element, and collection.
     *
     * @param {Object} formData - The data associated with the form, used for initialization.
     * @param {HTMLElement} parentElement - The parent HTML element where this component will be attached or interact with.
     * @param {Array|Object} collection - The collection or data structure to be managed or used.
     * @return {void}
     */
    constructor(formData, parentElement, collection) {
        super(formData, parentElement, collection);
    }

    /**
     * Handles the change event for a value input and updates the checkbox value or the general value
     * based on the current state of the component.
     *
     * @param {Event} event - The change event triggered by user interaction, containing the updated value.
     * @return {void} Updates the state value or checkbox value based on the event target's checked property.
     */
    valueChange(event) {
        if (this.value != null)  {
            return this.setCheckboxValue(event.target.checked ?? false);
        } else {
            return this.setValue(event.target.checked);
        }
    }

    /**
     * Updates the value of a checkbox based on the given `checkedValue`.
     * If the checkbox is part of a "multiselect" type control, it manages the
     * value in a list, adding or removing it based on the `checkedValue`.
     * Otherwise, sets the value as the current value or null.
     *
     * @param {boolean} checkedValue - Indicates whether the checkbox is checked (true) or unchecked (false).
     * @return {any} The updated value after the checkbox state is processed.
     */
    setCheckboxValue(checkedValue) {
        if (this.parent == null || this.parent.type !== "multiselect")  {
            return this.setValue(checkedValue === true ? this.value : null);
        }

        const dataValues = [...(this.dataValue ?? []).filter(item => item !== this.value)];
        if (checkedValue === true) {
            dataValues.push(this.value);
        }
        return this.setValue(dataValues);
    }

    /**
     * Wraps the component, providing additional DOM structure and styling.
     *
     * If the parent element exists, it uses the parent element as the wrapper.
     * Otherwise, it falls back to a default wrapper provided by the superclass.
     * Creates a label element with structured content and appends it to the wrapper.
     *
     * @return {HTMLElement} The label element that is appended to the wrapper.
     */
    componentWrapper() {
        let wrapper = this.parentElement;
        if (this.parent == null)  {
            wrapper = super.componentWrapper();
        }

        const labelElement = document.createElement('label');
        labelElement.classList.add('check-element');

        const labelDiv = document.createElement("div");
        labelDiv.appendChild(document.createTextNode(this.label ?? this.text));

        labelElement.appendChild(labelDiv);
        wrapper.appendChild(labelElement);

        return labelElement;
    }

    /**
     * Adds a new component to the specified wrapper by prepending it inside a newly created div element.
     *
     * @param {HTMLElement} wrapper - The parent HTML element to which the component will be added.
     * @param {HTMLElement} component - The child HTML element to be added inside the wrapper.
     * @return {void} This method does not return a value.
     */
    addComponent(wrapper, component) {
        const checkDiv = document.createElement("div");
        checkDiv.appendChild(component);
        wrapper.prepend(checkDiv);
    }

    /**
     * Sets the label for a specified wrapper element.
     *
     * @param {Object} wrapper - The wrapper element for which the label is to be set.
     */
    setLabel(wrapper) {}

    /**
     * Retrieves the input type associated with this method.
     *
     * @return {string} The input type, which is "checkbox".
     */
    inputType() {
        return "checkbox";
    }

    /**
     * Creates and configures a component by retrieving its base functionality,
     * applying dataset attributes, and setting its checked state.
     *
     * @return {HTMLElement} The configured component element.
     */
    component() {
        this.checkComponent = super.component();
        this.checkComponent.dataset.value = this.value;
        this.setChecked();

        return this.checkComponent;
    }

    /**
     * Sets the checked state of the component based on the `dataValue` and `value` properties.
     * Updates the `checked` property of the `checkComponent` based on the comparison logic.
     *
     * @return {void} Does not return a value.
     */
    setChecked() {
        if (this.value != null) {
            this.checkComponent.checked = Array.isArray(this.dataValue)
                ? this.dataValue.some(item => item === this.value)
                : this.value === this.dataValue;
        } else {
            this.checkComponent.checked = this.dataValue === true;
        }
    }
}

